import React from "react";
import { useNavigate } from "react-router-dom";
import getOptimizedUrl from "../utils/imageUtils";

const OrderConfirmationSidebar = ({ isOpen, onClose, order }) => {
  const navigate = useNavigate();
  const [mounted, setMounted] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const [countdown, setCountdown] = React.useState(10);

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
      setCountdown(10);
    } else if (mounted) {
      setClosing(true);
      setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, 300);
    }
  }, [isOpen, mounted]);

  React.useEffect(() => {
    if (!isOpen) return;

    if (countdown <= 0) {
      handleGoToOrders();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((current) => current - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isOpen, countdown]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
      onClose();
    }, 300);
  };

  const handleGoToOrders = () => {
    handleClose();
    setTimeout(() => navigate("/orders"), 320);
  };

  if (!mounted) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-[10000]"
        onClick={handleClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:max-w-md bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
          closing ? "drawer-slide-out" : "drawer-slide-in"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-end p-2 ">
            <button
              onClick={handleClose}
              className="text-black text-base font-bdogrotesk font-medium"
            >
              Close
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto no-scrollbar px-4 pb-6 space-y-6">
            {/* Title */}
            <p className="text-6xl font-medium font-bdogrotesk text-black leading-tight">
              Your Order is Confirmed!
            </p>

            {/* Item Images Grid — up to 3 columns, left-aligned */}
            {order?.items?.length > 0 && (
              <div className="flex gap-2 justify-start">
                {order.items.slice(0, 3).map((item, i) => (
                  item.image ? (
                    <div
                      key={i}
                      className="bg-white overflow-hidden "
                      style={{ width: order.items.length === 1 ? "35%" : "calc(33.333% - 6px)", }}
                    >
                      <img
                        src={getOptimizedUrl(item.image || "", 600)}
                        alt={item.productName}
                        className="w-48 h-48 object-cover bg-white"
                      />
                    </div>
                  ) : null
                ))}
              </div>
            )}

            {/* Subtitle */}
            <p className="text-black font-medium font-bdogrotesk">
              You will receive an email with your order confirmation soon. We'll keep you updated on the status of your order.
            </p>

            <p className="text-sm font-medium text-black font-bdogrotesk">
              Redirecting to your orders in {countdown} second{countdown === 1 ? '' : 's'}.
            </p>

            {/* Delivery details */}
           
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-gray-100 flex flex-col gap-3">
             {order?.shippingAddress && (
              <div className="font-bdogrotesk">
                <p className="text-xs uppercase tracking-widest text-black mb-0.5">Delivering to</p>
                <p className="text-base font-medium text-black">
                  {[order.shippingAddress.name, order.shippingAddress.phone].filter(Boolean).join(", ")}
                </p>
                <p className="text-base font-medium text-black">
                  {[order.shippingAddress.line1, order.shippingAddress.line2, order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.postalCode].filter(Boolean).join(", ")}
                </p>
              </div>
            )}
            {/* "View Order Details" — same as Add To Cart button */}
            <button
              type="button"
              onClick={handleGoToOrders}
              className="group w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide font-bdogrotesk"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex h-5 items-center">View Order Details</span>
                  <span className="flex h-5 items-center">View Order Details</span>
                </div>
              </div>
            </button>

            {/* "Continue Shopping" — same as Read More button in Blogs */}
            <button
              type="button"
              onClick={handleClose}
              style={{ border: "1px solid #000", boxSizing: "border-box", backgroundColor: "#fff" }}
              className="group w-full text-black bg-white px-3 py-2 flex items-center justify-between text-[15px] font-semibold tracking-wide font-bdogrotesk"
            >
              <div className="relative h-5 overflow-hidden flex-1">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex h-5 text-base items-center">Continue Shopping</span>
                  <span className="flex h-5 text-base items-center">Continue Shopping</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderConfirmationSidebar;
